import { useIntl } from '@weenat/wintl';
import differenceBy from 'lodash-es/differenceBy';
import isNil from 'lodash-es/isNil';
import { useClient, useMutation, useQuery } from '../..';
import { canUser } from '../access';
import appPaths from '../appPaths';
const STATION_ACTIONS_IDS = [
    'add_option',
    'complete_data',
    'create_alert',
    'geolocation',
    'remove',
    'rename',
    'toggle_favorite',
    'view_alerts',
    'view_forecasts',
    'view_history',
    'view_settings',
    'view_station',
    'open_in_new_tab'
];
const useStationActions = ({ stationId, currentOrgId, onGeolocationActionPress, onRemoveStationSuccess, onAddOptionActionPress, onCompleteDataActionPress, onRenameActionPress, excludedActions, newTabHandler }) => {
    const client = useClient();
    const { t } = useIntl();
    const stationRequester = client.stations.get(stationId);
    const stationQuery = useQuery(stationRequester, { enabled: !isNil(stationId) });
    const station = stationQuery.data;
    const orgRequester = client.orgs.get(currentOrgId);
    const orgRequest = useQuery(orgRequester, { enabled: !isNil(currentOrgId) });
    const org = orgRequest.data;
    const [toggleFavoriteMutation, toggleFavoriteRequest] = useMutation(client.stations.changeFavorite(stationId));
    const [removeFromOrg, removeStationRequest] = useMutation(client.stations.delete(stationId), {
        onSuccess: onRemoveStationSuccess
    });
    const groups = [
        {
            id: 'access',
            actions: []
        },
        {
            id: 'edition',
            actions: []
        },
        {
            id: 'my_farm',
            actions: []
        },
        {
            id: 'local_actions',
            actions: []
        },
        {
            id: 'deletion',
            actions: []
        }
    ];
    if (!isNil(station) && !isNil(currentOrgId) && !isNil(org)) {
        const canView = canUser('view', 'stations', { organization: org });
        const canEdit = canUser('edit', 'stations', { organization: org });
        if (canView) {
            groups[0].actions.push({
                id: 'view_station',
                label: t('models.station.actions.view_station', { capitalize: true }),
                to: appPaths.plotsMap.stationSummary(station.organizationId, station.id),
                iconName: 'Preview'
            }, {
                id: 'view_history',
                label: t('actions.view_history', { capitalize: true }),
                to: appPaths.plotsMap.stationHistory(station.organizationId, station.id),
                iconName: 'History'
            }, {
                id: 'view_forecasts',
                label: t('actions.view_forecasts', { capitalize: true }),
                to: appPaths.plotsMap.stationForecast(station.organizationId, station.id),
                iconName: 'Weather'
            }, {
                id: 'view_alerts',
                label: t('actions.view_alerts', { capitalize: true }),
                to: appPaths.plotsMap.stationAlerts(station.organizationId, station.id),
                iconName: 'Notifications'
            }, {
                id: 'create_alert',
                label: t('alerts.create', { capitalize: true }),
                to: appPaths.alerts.create,
                search: { stationId: station.id },
                iconName: 'NotificationAdd'
            });
            groups[1].actions.push({
                id: 'toggle_favorite',
                label: t(`actions.${station.isFavorite ? 'remove_from_favorite' : 'add_to_favorite'}`, {
                    capitalize: true
                }),
                onPress: async () => {
                    await toggleFavoriteMutation({ isFavorite: !station.isFavorite });
                },
                state: toggleFavoriteRequest,
                iconName: 'Star'
            });
            if (canEdit) {
                groups[1].actions.push({
                    id: 'view_settings',
                    label: t('titles.settings', { capitalize: true }),
                    to: appPaths.admin.stationSettings(currentOrgId, station.id),
                    iconName: 'SettingsCog'
                });
                if (!isNil(onRenameActionPress)) {
                    groups[1].actions.push({
                        id: 'rename',
                        label: t('actions.rename', { capitalize: true }),
                        onPress: onRenameActionPress,
                        iconName: 'Edit'
                    });
                }
                if (!isNil(onAddOptionActionPress)) {
                    groups[2].actions.push({
                        id: 'add_option',
                        label: t('models.option.actions.add_option', { capitalize: true }),
                        onPress: onAddOptionActionPress,
                        iconName: 'AddOption'
                    });
                }
                if (!isNil(onCompleteDataActionPress)) {
                    groups[2].actions.push({
                        id: 'complete_data',
                        label: t('actions.complete_data', { capitalize: true }),
                        onPress: onCompleteDataActionPress,
                        iconName: 'DataSaverOn'
                    });
                }
            }
            if (!isNil(onGeolocationActionPress)) {
                groups[3].actions.push({
                    id: 'geolocation',
                    label: t(!isNil(station.location)
                        ? 'models.device.actions.locate_short'
                        : 'errors.no_gps_coordinates_found'),
                    onPress: onGeolocationActionPress,
                    iconName: 'LocationPin',
                    isDisabled: isNil(station.location)
                });
            }
            if (!isNil(newTabHandler)) {
                groups[3].actions.push({
                    id: 'open_in_new_tab',
                    label: t('actions.open_in_new_tab'),
                    onPress: newTabHandler,
                    iconName: 'OpenInNew'
                });
            }
            if (canEdit) {
                groups[4].actions.push({
                    id: 'remove',
                    label: t('models.station.actions.remove', { capitalize: true }),
                    onPress: async () => {
                        await removeFromOrg();
                    },
                    state: removeStationRequest,
                    iconName: 'Bin',
                    isDanger: true,
                    needConfirmation: true
                });
            }
        }
    }
    if (!isNil(excludedActions)) {
        const excludedActionsIds = [];
        for (const excludedAction of excludedActions.values()) {
            excludedActionsIds.push({ id: excludedAction });
        }
        for (const group of groups) {
            group.actions = differenceBy(group.actions, excludedActionsIds, 'id');
        }
    }
    return groups;
};
export default useStationActions;
